import { UserSwitchOutlined } from "@ant-design/icons";
import { Badge, Button, Input, Popconfirm, Space, Table, Tag } from 'antd';
import { motion } from 'framer-motion';
import { debounce } from 'lodash';
import { Edit, Plus, Search, Trash } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import UserModal from '../../components/UserModal';
import { getRoleName, pageVariants, roleOptions } from '../../constants';
import { getRoutes } from '../../constants/Routes';
import useAuth from '../../hooks/useAuth';
import { sendAccountApprovedEmail, sendUserCreds } from '../../redux/actions/email';
import { addUser, deleteUser, getUsers, updateUser } from '../../redux/actions/users';
import { generatePassword, getFullName } from '../../utils';

const UsersScreen = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { users, usersPagination: pagination } = useSelector((state) => state.usersSlice);
    const [open, setOpen] = useState(false);
    const [data, setData] = useState();
    const [search, setSearch] = useState('');

    const renderHighlightedText = (text) => (
        <Highlighter
            highlightClassName="highlighted-text"
            searchWords={[search]}
            autoEscape
            textToHighlight={text ? text.toString() : ''}
        />
    );

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 40,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (_, record) => {
                const name = getFullName(record);
                return renderHighlightedText(name);
            },
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: renderHighlightedText,
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            filters: roleOptions.map((role) => ({ text: role.label, value: role.value })),
            onFilter: (value, record) => record.role === value,
            render: (role) => getRoleName(role),
        },
        {
            title: 'Lead',
            dataIndex: 'lead_name',
            key: 'lead_name',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: 'Active', value: 1 },
                { text: 'In Active', value: 0 },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status) => {
                return (
                    <Tag className="status-tag" color={status ? 'green' : 'red'}>
                        <Badge color={status ? 'green' : 'red'} />
                        <span>{status ? 'Active' : 'Inactive'}</span>
                    </Tag>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'end',
            width: 100,
            hidden: user?.role === 1,
            render: (_, record) =>
                user.id !== record?.id && (
                    <Space>
                        <Edit
                            size={18}
                            color="#6B7280"
                            cursor="pointer"
                            onClick={() => {
                                setOpen(true);
                                setData(record);
                            }}
                        />
                        <Popconfirm
                            title="Delete User"
                            description="Are you sure to delete this user?"
                            onConfirm={() => handleDeleteUser(record?.id)}
                        >
                            <Trash size={18} color="#6B7280" cursor="pointer" />
                        </Popconfirm>
                    </Space>
                ),
        },
    ];

    const fetchUsers = useCallback(
        debounce((searchValue) => {
            getUsers({ searchText: searchValue });
        }, 500),
        []
    );

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
        fetchUsers(value);
    };

    const handlePaginationChange = async (page) => {
        await getUsers({ page });
    };

    const handleTableChange = async (pagination, filters, sorter) => {
        await getUsers({
            role: filters?.role?.length ? filters?.role[0] : '',
            status: filters?.status?.length ? filters?.status[0] : '',
        });
    };

    const handleSubmit = async (values) => {
        if (!values?.lead_id) values.lead_id = null;
        if (!data) values.password = generatePassword(16);
        const res = data ? await updateUser({ ...values, id: data?.id }) : await addUser(values);

        if (res?.ack && !data) {
            sendUserCreds({
                userName: `${values?.first_name} ${values?.last_name ?? ''}`,
                email: values?.email,
                password: values?.password,
            });
        }

        if (data && res?.ack && values?.status === 1 && data?.status === 0) {
            sendAccountApprovedEmail({
                user: {
                    first_name: values?.first_name,
                    last_name: values?.last_name ?? '',
                },
                userEmail: values?.email,
            });
        }

        if (res?.ack) handleCancel();
    };

    const handleCancel = () => {
        setData();
        setOpen(false);
    };

    const handleDeleteUser = (id) => {
        deleteUser({
            id,
        });
    };

    useEffect(() => {
        getUsers();
    }, []);

    return (
        <motion.div
            className="Users"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={pageVariants}
        >
            <div className="header-flex">
                <div className="page-title">Users</div>
                <Space>
                    {user?.role !== 2 && (
                        <Button
                            variant="filled"
                            color="cyan"
                            size="large"
                            icon={<UserSwitchOutlined style={{ fontSize: 20 }} />}
                            onClick={() => navigate(getRoutes().alternate_users.route())}
                            title="Alternate Users"
                        />
                    )}
                    {user?.role === 0 && (
                        <Button
                            variant="solid"
                            color="primary"
                            size="large"
                            icon={<Plus />}
                            onClick={() => setOpen(true)}
                        >
                            Add User
                        </Button>
                    )}
                </Space>
            </div>
            <div className="theme-bg shadow">
                <motion.div
                    className="filter-header p-6"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.3 } }}
                >
                    <Input
                        placeholder="Search..."
                        size="large"
                        allowClear
                        prefix={<Search size={18} color="rgb(156,163,175)" />}
                        value={search}
                        onChange={handleSearch}
                    />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
                >
                    <Table
                        columns={columns}
                        dataSource={users}
                        rowKey="id"
                        onChange={handleTableChange}
                        components={{
                            header: {
                                cell: (props) => <th {...props} className="table-header px-2" />,
                            },
                            body: {
                                cell: (props) => <td {...props} className="theme-bg px-2 py-1" />,
                            },
                        }}
                        pagination={{
                            current: pagination?.page,
                            pageSize: pagination?.limit,
                            total: pagination?.total,
                            showSizeChanger: false,
                            onChange: handlePaginationChange,
                            position: ['bottomCenter'],
                        }}
                        scroll={{ x: 'max-content', y: 'calc(100dvh - 360px)' }}
                    />
                </motion.div>
            </div>
            {open && (
                <UserModal open={open} onCancel={handleCancel} data={data} onSubmit={handleSubmit} />
            )}
        </motion.div>
    );
};

export default UsersScreen;
